import { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DirectionsRunIcon from "@mui/icons-material/DirectionsRun";
import RefreshIcon from "@mui/icons-material/Refresh";
import axios from "axios";
import JSZip from "jszip";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../../../style/setup.css";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "80%",
  bgcolor: "background.paper",
  border: "0px solid #000",
  boxShadow: 24,
  p: 4,
};

const tableCellStyle = {
  whiteSpace: "nowrap",
  backgroundImage: "linear-gradient(#0ab9cc, #069dae)",
  border: "1px solid hsl(186, 89.90%, 27.30%)",
  color: "#FFF",
  fontWeight: "bold",
  position: "sticky",
  top: 0,
  zIndex: 1000,
  minWidth: 120,
  textAlign: "center",
};

export const AllRetailersUploadModal = ({
  open,
  onClose,
  accessToken,
  currentPeriod,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const itemsPerPageList = [5, 10, 50, 100];
  const [totalItems, setTotalItems] = useState("");
  const [dataCellStoreMapping, setDataCellStoreMapping] = useState([]);

  const [selectedFiles, setSelectedFiles] = useState({});
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [inputValue, setInputValue] = useState("");

  async function fetchMainData() {
    setDataCellStoreMapping([]);
    setIsLoading(true);

    const queryParams = new URLSearchParams({
      page: currentPage,
      size: itemsPerPage,
      period: selectedPeriod,
      category: "by_period",
    });

    const apiUrl = `${process.env.REACT_APP_API_URL}/api/v1/data-processing-raw/rta_mt/retailer/check?${queryParams}`;

    try {
      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setTotalItems(data.pagination_info.total);
        setDataCellStoreMapping(data.data);
      } else {
        setDataCellStoreMapping([]);
      }
    } catch (error) {
      console.error("Failed to fetch retailer data:", error);
      setDataCellStoreMapping([]);
      toast.error("Failed to fetch retailer data.");
    } finally {
      setIsLoading(false);
    }
  }

  const handleFileSelect = (event, retailer) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFiles((prevFiles) => ({
        ...prevFiles,
        [retailer]: file,
      }));
    }
  };

  const handleUploadAll = async () => {
    const filesToUpload = Object.entries(selectedFiles);
    if (filesToUpload.length === 0) {
      toast.warn("Please select at least one file to upload.");
      return;
    }

    const validationErrors = [];
    filesToUpload.forEach(([retailer, file]) => {
      const fileNameWithoutExt =
        file.name.substring(0, file.name.lastIndexOf(".")) || file.name;
      const expectedFileName = `${retailer}_${selectedPeriod}`;

      if (fileNameWithoutExt !== expectedFileName) {
        validationErrors.push(retailer);
      }
    });

    if (validationErrors.length > 0) {
      const errorRetailers = validationErrors.join(", ");
      toast.error(
        `Error: "${errorRetailers}" is invalid. The filename must include the correct retailer and current period. Example: Retailer_${selectedPeriod}.xlsx.`
      );
      return;
    }

    setIsLoading(true);

    try {
      const zip = new JSZip();

      Object.values(selectedFiles).forEach((file) => {
        zip.file(file.name, file);
      });

      const zipBlob = await zip.generateAsync({ type: "blob" });

      const formData = new FormData();
      formData.append("zip_file", zipBlob, "retailers-upload.zip");

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/v1/data-processing-single/data_preparation/retailer_data/upload/all/rta_mt`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          body: formData,
        }
      );

      const message = await response.json();

      if (response.ok) {
        toast.success(message.detail || "All files uploaded successfully!");
        fetchMainData();
        setSelectedFiles({});
      } else {
        throw new Error(message.detail || "Upload failed.");
      }
    } catch (error) {
      toast.error(`Failed to upload zip file: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (event, page) => {
    setCurrentPage(page + 1);
  };

  const handleChangeItemPerPageSelect = (e) => {
    setItemsPerPage(e.target.value);
    setCurrentPage(1);
  };

  const handleCloseModal = () => {
    onClose();
    setDataCellStoreMapping([]);
    setSelectedFiles({});
    setSelectedPeriod("");
    setCurrentPage(1);
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSelectedPeriod(inputValue);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [inputValue]);

  useEffect(() => {
    if (currentPeriod) {
      setSelectedPeriod(currentPeriod);
      setInputValue(currentPeriod);
    }
  }, [open]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (open && selectedPeriod) {
        fetchMainData();
      }
    }, 200);
    return () => clearTimeout(handler);
  }, [open, accessToken, selectedPeriod, currentPage, itemsPerPage]);

  return (
    <Modal
      open={open}
      onClose={handleCloseModal}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      sx={{ border: 0 }}
    >
      <Box
        sx={{
          ...style,
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          borderRadius: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
            <Typography
              id="modal-modal-title"
              variant="h6"
              component="h2"
              sx={{ mr: 1 }}
            >
              All Retailers Upload -
            </Typography>
            <TextField
              id="outlined-basic"
              variant="outlined"
              type="number"
              size="small"
              sx={{ ml: 0 }}
              InputProps={{ sx: { borderRadius: 0 } }}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
            />
          </Box>
          <IconButton onClick={handleCloseModal}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ flexGrow: 1, overflow: "auto" }}>
          <Paper sx={{ width: "100%", overflow: "hidden" }}>
            <TableContainer sx={{ maxHeight: 480 }}>
              <Table
                stickyHeader
                aria-label="simple table"
                sx={{
                  border: 1,
                  borderColor: "grey.300",
                  borderCollapse: "collapse",
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell style={tableCellStyle}>Retailer</TableCell>
                    <TableCell style={tableCellStyle}>Flag</TableCell>
                    <TableCell style={tableCellStyle}>Record</TableCell>
                    <TableCell style={tableCellStyle}>Process At</TableCell>
                    <TableCell style={tableCellStyle}>File Name</TableCell>
                    <TableCell style={tableCellStyle}>Choose File</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading && dataCellStoreMapping.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : dataCellStoreMapping.length > 0 ? (
                    dataCellStoreMapping.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell align="center">{row.retailer}</TableCell>
                        <TableCell align="center">
                          <span
                            style={{
                              backgroundColor:
                                row.flag === "filled" ? "#d0f0c0" : "#fddede",
                              color:
                                row.flag === "filled" ? "#2e7d32" : "#c62828",
                              padding: "2px 8px",
                              borderRadius: "4px",
                              fontWeight: 500,
                              display: "inline-block",
                              minWidth: "60px",
                            }}
                          >
                            {row.flag}
                          </span>
                        </TableCell>
                        <TableCell align="center">{row.total_record}</TableCell>
                        <TableCell align="center">{row.process_at || "-"}</TableCell>
                        <TableCell
                          align="left"
                          sx={{
                            wordBreak: "break-all",
                            fontWeight: `${
                              selectedFiles[row.retailer] ? "bold" : "normal"
                            }`,
                          }}
                        >
                          {selectedFiles[row.retailer]?.name ||
                            "No file selected"}
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            component="label"
                            sx={{
                              backgroundImage:
                                "linear-gradient(#e4e4e4ff, #d5d5d5ff)",
                              border: "1px solid hsla(0, 0%, 56%, 1.00)",
                              borderRadius: "4px",
                              color: "#6c6c6cff",
                              fontSize: { xs: "12px", sm: "14px" },
                              padding: "0px 10px",
                              textTransform: "none",
                              "&:hover": {
                                backgroundImage:
                                  "linear-gradient(#b3b3b3ff, #9e9e9eff)",
                              },
                            }}
                            size="small"
                          >
                            Select File
                            <input
                              type="file"
                              hidden
                              onChange={(e) =>
                                handleFileSelect(e, row.retailer)
                              }
                              accept=".csv, .xlsx, .xls"
                            />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No Data Available
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {dataCellStoreMapping.length > 0 && (
              <TablePagination
                rowsPerPageOptions={itemsPerPageList}
                component="div"
                count={totalItems}
                rowsPerPage={itemsPerPage}
                page={currentPage - 1}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleChangeItemPerPageSelect}
              />
            )}
          </Paper>
        </Box>
        <Box
          sx={{ display: "flex", justifyContent: "flex-end", mt: 3, gap: 2 }}
        >
          <Button
            sx={{
              backgroundImage: "linear-gradient(#0ab9cc, #069dae)",
              border: "1px solid hsl(186, 89.90%, 27.30%)",
              borderRadius: "4px",
              color: "#fff",
              fontSize: { xs: "14px", sm: "16px" },
              padding: "4px 10px",
              textTransform: "none",
              "&:hover": {
                backgroundImage: "linear-gradient(#0991a2, #057d8c)",
              },
            }}
            startIcon={<RefreshIcon />}
            onClick={() => fetchMainData()}
            disabled={isLoading}
          >
            Refresh
          </Button>
          <Button
            sx={{
              backgroundImage: "linear-gradient(#0ab9cc, #069dae)",
              border: "1px solid hsl(186, 89.90%, 27.30%)",
              borderRadius: "4px",
              color: "#fff",
              fontSize: { xs: "14px", sm: "16px" },
              padding: "4px 10px",
              textTransform: "none",
              "&:hover": {
                backgroundImage: "linear-gradient(#0991a2, #057d8c)",
              },
            }}
            startIcon={<DirectionsRunIcon />}
            onClick={handleUploadAll}
            disabled={Object.keys(selectedFiles).length === 0 || isLoading}
          >
            Upload All Retailers
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
